import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  VideoCall,
  Email,
  CheckCircle,
  School,
  Group,
  ManageAccounts,
  AccessTime,
  Info,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';

const ApplyAmbassadorPage: React.FC = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = React.useState(0);

  const steps = [
    {
      label: 'Create Your Introduction Video',
      icon: <VideoCall />,
      description: 'Record a video introducing yourself and explaining why you want to become an ambassador',
      details: [
        'Introduce yourself (name, branch, year)',
        'Explain why you want to become a Unii Club Ambassador',
        'Share your vision for growing the club community',
        'Duration: 2-5 minutes',
        'Format: MP4, MOV, or any standard video format',
      ],
    },
    {
      label: 'Showcase Your Club Management Skills',
      icon: <ManageAccounts />,
      description: 'Explain how you would manage and grow clubs on campus',
      details: [
        'Which clubs would you like to create or manage?',
        'How will you attract students to join these clubs?',
        'What strategies will you use to keep members engaged?',
        'How will you handle club activities and events?',
        'Share any past leadership or management experience',
      ],
    },
    {
      label: 'Demonstrate Your Growth Strategy',
      icon: <Group />,
      description: 'Show us how you plan to bring users to your clubs',
      details: [
        'How will you promote clubs to students on campus?',
        'What marketing or outreach methods will you use?',
        'How will you leverage social media and word-of-mouth?',
        'What unique approaches do you have for student engagement?',
        'How will you measure and track club growth?',
      ],
    },
    {
      label: 'Send Your Application',
      icon: <Email />,
      description: 'Email your application to our team',
      details: [
        'Send your video and responses to: devashish@unii.club',
        'Include your email address in the message',
        'Mention your college name',
        'Add your phone number for quick contact',
        'Subject line: "Ambassador Application - [Your Name]"',
      ],
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Become a Unii Club Ambassador
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Lead, Inspire, and Grow Your Campus Community
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<School />}
            label="Students Only"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            icon={<AccessTime />}
            label="Response: 1-2 days (typically)"
            color="success"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert
        severity="info"
        icon={<Info />}
        sx={{ mb: 4, borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          What is an Ambassador?
        </Typography>
        <Typography variant="body2">
          Ambassadors are student leaders who manage clubs, create engaging content, organize events,
          and help grow the Unii Club community on their campus. You'll get special privileges,
          recognition, and the opportunity to make a real impact!
        </Typography>
      </Alert>

      {/* Application Steps */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Application Process
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: activeStep >= index ? 'primary.main' : 'grey.300',
                      color: 'white',
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography sx={{ mb: 2, color: 'text.secondary' }}>
                  {step.description}
                </Typography>

                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <List dense>
                      {step.details.map((detail, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={detail}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {index === steps.length - 1 ? 'Finish' : 'Continue'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3, bgcolor: 'success.light', borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 2, color: 'success.dark' }}>
              You're all set! 🎉
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Review the steps above and prepare your application. Once ready, send your video and
              details to{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                devashish@unii.club
              </Typography>
            </Typography>
            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }} variant="outlined">
              Review Steps Again
            </Button>
          </Paper>
        )}
      </Paper>

      {/* Quick Summary Card */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
          Quick Application Summary
        </Typography>

        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            📧 Email Address:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            devashish@unii.club
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            📝 What to Include:
          </Typography>
          <List dense>
            {[
              'Video introduction (2-5 minutes)',
              'Your email address',
              'Your phone number',
              'Your college name',
              'Responses to all the points mentioned above',
            ].map((item, idx) => (
              <ListItem key={idx} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            ⏱️ Response Time:
          </Typography>
          <Typography variant="body1">
            Typically within 1-2 days, maximum 3-5 days
          </Typography>
        </Box>
      </Paper>

      {/* Current User Info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Applying as: <strong>{user?.name}</strong> ({user?.email})
        </Typography>
      </Box>
    </Container>
  );
};

export default ApplyAmbassadorPage;
